import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { ContractCopyEntity } from "../../types/ContractCopyEntity";
import type { ContractEntity } from "../../types/ContractEntity";
import { ContractCopyEntityToCopyDto } from "../mappers/ContractCopyEntityToCopyDto";
import { ContractDtoToEntity } from "../mappers/ContractDtoToEntity";
import { ContractEntityToDto } from "../mappers/ContractEntityToDto";

const ContractApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        ContractPhysicalLoad: builder.query<ContractEntity, FormQuery>({
            providesTags: [ResourceName.CONTRACTS],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_PHYSICAL}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return ContractDtoToEntity(response);
            },
        }),
        ContractPhysicalSave: builder.mutation<ContractEntity, FormMutation<ContractEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_PHYSICAL}`;
                const method = "POST";

                const body = ContractEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CONTRACTS],
        }),
        ContractPhysicalUpdate: builder.mutation<ContractEntity, FormMutation<ContractEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_PHYSICAL}/${mutation.data.id}`;
                const method = "PUT";

                const body = ContractEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CONTRACTS],
        }),
        ContractVirtualLoad: builder.query<ContractEntity, FormQuery>({
            providesTags: [ResourceName.CONTRACTS_VIRTUAL],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_VIRTUAL}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return ContractDtoToEntity(response);
            },
        }),
        ContractVirtualSave: builder.mutation<ContractEntity, FormMutation<ContractEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_VIRTUAL}`;
                const method = "POST";

                const body = ContractEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CONTRACTS_VIRTUAL],
        }),
        ContractVirtualUpdate: builder.mutation<ContractEntity, FormMutation<ContractEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS_VIRTUAL}/${mutation.data.id}`;
                const method = "PUT";

                const body = ContractEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CONTRACTS_VIRTUAL],
        }),
        ContractCopy: builder.mutation<ContractEntity, FormMutation<ContractCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CONTRACTS}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = ContractCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CONTRACTS],
        }),
    }),
});

export const {
    useContractPhysicalLoadQuery,
    useContractPhysicalSaveMutation,
    useContractPhysicalUpdateMutation,
    useContractVirtualLoadQuery,
    useContractVirtualSaveMutation,
    useContractVirtualUpdateMutation,
    useContractCopyMutation,
} = ContractApi;
